import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Calendar, FileText, Plus } from "lucide-react";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import { db } from "../../db";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import NoteComposer from "./NoteComposer";
import VisitNotesGroup from "./VisitNotesGroup";
import { buildPatientNotesTimeline, getEligibleNoteAppointments, selectDefaultNoteAppointment } from "./patientNotesTimelineModel";
import { useNoteActions } from "./useNoteActions";

import AssessmentComposer from "../customForms/AssessmentComposer";
import { buildClinicalItems, splitClinicalItems } from "./clinicalItems";
import { syncService } from "../../services/SyncService";

export default function PatientNotesTimeline({ appointments, userId, token, onScheduleAppointment, now = null }) {
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState('note'); // 'note' | 'assessment'
  const [correctionAssessment, setCorrectionAssessment] = useState(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState("");
  const [showEnteredInError, setShowEnteredInError] = useState(false);
  const newNoteButtonRef = useRef(null);
  const appointmentIds = useMemo(() => appointments.map(item => item.id), [appointments]);
  const notes = useLiveQuery(
    () => (appointmentIds.length ? db.appointment_notes.where("appointment_id").anyOf(appointmentIds).toArray() : []),
    [appointmentIds.join("|")]
  ) || [];
  const assessments = useLiveQuery(
    () => (appointmentIds.length ? db.form_assessments.where("appointment_id").anyOf(appointmentIds).toArray() : []),
    [appointmentIds.join("|")]
  ) || [];
  const assessmentIds = useMemo(() => assessments.map(item => item.id), [assessments]);
  const revisions = useLiveQuery(
    () => (assessmentIds.length ? db.form_assessment_revisions.where("assessment_id").anyOf(assessmentIds).toArray() : []),
    [assessmentIds.join("|")]
  ) || [];
  const templates = useLiveQuery(() => db.form_templates.toArray(), []) || [];
  const versions = useLiveQuery(() => db.form_template_versions.toArray(), []) || [];
  const users = useLiveQuery(() => db.users.toArray(), []) || [];
  const clinics = useLiveQuery(() => db.clinics.toArray(), []) || [];
  const actions = useNoteActions({ userId, token });
  const referenceNow = useMemo(() => now || new Date(), [now]);

  const clinicalItems = useMemo(
    () => buildClinicalItems({ notes, assessments, revisions, templates, versions, users }),
    [notes, assessments, revisions, templates, versions, users]
  );

  const { defaultItems, enteredInErrorItems } = useMemo(
    () => splitClinicalItems(clinicalItems),
    [clinicalItems]
  );

  const visibleItems = useMemo(
    () => (showEnteredInError ? clinicalItems : defaultItems),
    [showEnteredInError, clinicalItems, defaultItems]
  );

  const groups = useMemo(
    () => buildPatientNotesTimeline({ appointments, items: visibleItems, users, clinics }),
    [appointments, visibleItems, users, clinics]
  );
  const eligible = useMemo(() => getEligibleNoteAppointments(appointments), [appointments]);

  useEffect(() => {
    if (composerOpen) return;
    if (!eligible.some(item => item.id === selectedAppointmentId)) {
      setSelectedAppointmentId(selectDefaultNoteAppointment(eligible, referenceNow)?.id || "");
    }
  }, [composerOpen, eligible, selectedAppointmentId, referenceNow]);

  const closeComposer = () => {
    setComposerOpen(false);
    setComposerMode('note');
    setCorrectionAssessment(null);
    requestAnimationFrame(() => newNoteButtonRef.current?.focus());
  };

  const beforeCreate = async appointmentId => {
    if (!appointments.some(appointment => appointment.id === appointmentId)) {
      toast.error("That appointment is not available in the selected mode. Choose another appointment.");
      return false;
    }

    const appointment = await db.appointments.get(appointmentId);
    if (!appointment || appointment.status === "cancelled") {
      toast.error("That appointment is no longer available. Choose another appointment.");
      return false;
    }
    return true;
  };

  const handleEnteredInError = async (assessment, reason) => {
    const queueRecord = {
      type: 'create',
      entity: 'form_assessment_entered_in_error',
      entity_id: assessment.id,
      data: { assessment_id: assessment.id, reason },
      status: 'pending',
      created_at: new Date().toISOString(),
    };
    await db.transaction('rw', db.form_assessments, db.sync_queue, async () => {
      await db.form_assessments.update(assessment.id, {
        status: 'entered_in_error',
        entered_in_error_reason: reason,
        entered_in_error_by: userId,
        entered_in_error_at: new Date().toISOString(),
        sync_status: 'pending',
        _synced: false,
      });
      await db.sync_queue.add(queueRecord);
    });
    syncService.processQueue();
  };

  const handleCorrect = (assessment, revision) => {
    setCorrectionAssessment({ ...assessment, answers: revision.answers });
    setSelectedAppointmentId(assessment.appointment_id);
    setComposerMode('assessment');
    setComposerOpen(true);
  };

  if (appointments.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
        <Calendar className="mx-auto mb-3 text-slate-400" />
        <h2 className="font-semibold text-slate-900">Schedule an appointment to start clinical notes</h2>
        <p className="mt-2 text-sm text-slate-500">Every clinical note must belong to an appointment.</p>
        <Button className="mt-4" onClick={onScheduleAppointment}>Schedule appointment</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Clinical timeline</h2>
          <p className="text-sm text-slate-500">{groups.length} visits with notes</p>
        </div>
        <div className="flex items-center gap-2">
          {enteredInErrorItems.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowEnteredInError(v => !v)}>
              {showEnteredInError ? 'Hide' : 'Show'} entries marked in error ({enteredInErrorItems.length})
            </Button>
          )}
          <Button ref={newNoteButtonRef} onClick={() => { setComposerMode('note'); setComposerOpen(true); }} disabled={!selectedAppointmentId}>
            <Plus size={16} /> New note
          </Button>
        </div>
      </header>
      {composerOpen && selectedAppointmentId && (
        <section className="rounded-2xl border border-blue-200 bg-blue-50 p-4 md:p-6" aria-label="Create clinical note">
          <div className="mb-4 max-w-sm">
            <Label htmlFor="note-appointment">Appointment</Label>
            <Select value={selectedAppointmentId} onValueChange={setSelectedAppointmentId}>
              <SelectTrigger id="note-appointment" aria-label="Appointment"><SelectValue /></SelectTrigger>
              <SelectContent>
                {eligible.map(item => (
                  <SelectItem key={item.id} value={item.id}>
                    {new Date(item.date_time).toLocaleString()} · {item.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {composerMode === 'assessment' ? (
            <AssessmentComposer
              appointmentId={selectedAppointmentId}
              userId={userId}
              correctionAssessment={correctionAssessment}
              onClose={closeComposer}
              onSubmitted={closeComposer}
            />
          ) : (
            <NoteComposer
              appointmentId={selectedAppointmentId}
              actions={actions}
              beforeCreate={beforeCreate}
              onCancel={closeComposer}
              onStartAssessment={() => setComposerMode('assessment')}
              autoFocus
            />
          )}
        </section>
      )}
      {groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
          <FileText className="mx-auto mb-3 text-slate-400" />
          <p className="text-slate-500">No clinical notes yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map(group => (
            <VisitNotesGroup
              key={group.appointment.id}
              appointment={group.appointment}
              notes={group.notes}
              items={group.items}
              userId={userId}
              token={token}
              actions={actions}
              onCorrectAssessment={handleCorrect}
              onEnteredInErrorAssessment={handleEnteredInError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

PatientNotesTimeline.propTypes = {
  appointments: PropTypes.arrayOf(PropTypes.object).isRequired,
  userId: PropTypes.string.isRequired,
  token: PropTypes.string,
  onScheduleAppointment: PropTypes.func.isRequired,
  now: PropTypes.instanceOf(Date),
};
